(() => {
  if (customElements.get('cart-drawer')) return;

  const oldNotification = document.querySelector('cart-notification');
  if (oldNotification) oldNotification.parentNode.removeChild(oldNotification);

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.cartUpdateUnsubscriber = null;
      this.cartCountPromise = null;
      this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
      this.bindOverlay();
      this.setHeaderCartIconAccessibility();
      this.upgradeTrustProof();
    }

    connectedCallback() {
      this.bindOverlay();
      this.setHeaderCartIconAccessibility();
      this.upgradeTrustProof();

      if (!this.cartUpdateUnsubscriber && typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event = {}) => {
          const count = event.cartData?.item_count;
          if (Number.isFinite(count)) this.syncHeaderCartCount(count);
          else this.refreshHeaderCartCount();
          queueMicrotask(() => this.upgradeTrustProof());
        });
      }
    }

    disconnectedCallback() {
      this.cartUpdateUnsubscriber?.();
      this.cartUpdateUnsubscriber = null;
    }

    bindOverlay() {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay && !overlay.dataset.fmBound) {
        overlay.dataset.fmBound = 'true';
        overlay.addEventListener('click', () => this.close());
      }
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink || cartLink.dataset.fmDrawerBound === 'true') return;
      cartLink.dataset.fmDrawerBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code === 'Space' || event.key === ' ') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    syncHeaderCartCount(itemCount) {
      const count = Math.max(0, Number.parseInt(itemCount, 10) || 0);
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;

      let badge = cartLink.querySelector('.fm-header__count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'fm-header__count';
          cartLink.appendChild(badge);
        }
        badge.textContent = count > 99 ? '99+' : String(count);
        badge.hidden = false;
        cartLink.setAttribute('aria-label', `Cart, ${count} item${count === 1 ? '' : 's'}`);
      } else {
        badge?.remove();
        cartLink.setAttribute('aria-label', 'Cart');
      }
    }

    refreshHeaderCartCount() {
      if (this.cartCountPromise) return this.cartCountPromise;
      const cartUrl = `${window.routes?.cart_url || '/cart'}.json`;
      this.cartCountPromise = fetch(cartUrl, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(`Cart count request failed: ${response.status}`);
          return response.json();
        })
        .then((cart) => {
          if (Number.isFinite(cart?.item_count)) this.syncHeaderCartCount(cart.item_count);
          return cart;
        })
        .catch((error) => console.warn('[FurMily cart count]', error))
        .finally(() => {
          this.cartCountPromise = null;
        });
      return this.cartCountPromise;
    }

    upgradeTrustProof() {
      // Trust proof is rendered server-side in cart-drawer.liquid.
      // Keeping this method as a no-op preserves existing callers without DOM duplication.
    }

    open(triggeredBy) {
      if (this.classList.contains('active')) return;
      if (triggeredBy) this.setActiveElement(triggeredBy);
      this.setHeaderCartIconAccessibility();
      requestAnimationFrame(() => this.classList.add('animate', 'active'));
      document.body.classList.add('overflow-hidden');
      this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
      window.setTimeout(() => {
        if (!this.classList.contains('active')) return;
        const focusContainer = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('.drawer__inner');
        const focusElement = this.querySelector('.drawer__close') || focusContainer;
        if (focusContainer && focusElement && typeof trapFocus === 'function') trapFocus(focusContainer, focusElement);
      }, 220);
    }

    close() {
      if (!this.classList.contains('active')) return;
      this.classList.remove('active');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }

    renderContents(parsedState) {
      if (!parsedState?.sections) return false;
      this.productId = parsedState.id;
      let rendered = false;

      this.getSectionsToRender().forEach((section) => {
        const sectionHtml = parsedState.sections[section.id];
        if (!sectionHtml) return;
        const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (!target) return;
        const replacement = this.getSectionInnerHTML(sectionHtml, section.selector);
        if (replacement != null) {
          target.innerHTML = replacement;
          rendered = true;
        }
      });

      if (!rendered) return false;
      this.bindOverlay();
      this.upgradeTrustProof();
      this.classList.remove('is-empty');
      this.refreshHeaderCartCount();
      this.open();
      return true;
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      if (!html) return null;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const node = selector ? parsed.querySelector(selector) : parsed.body;
      return node?.innerHTML ?? null;
    }

    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '#CartDrawer' }];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);

  if (!customElements.get('cart-drawer-items') && window.CartItems) {
    class CartDrawerItems extends window.CartItems {
      getSectionsToRender() {
        return [{ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' }];
      }
    }
    customElements.define('cart-drawer-items', CartDrawerItems);
  }

  document.addEventListener('shopify:section:load', () => {
    const drawer = document.querySelector('cart-drawer');
    drawer?.setHeaderCartIconAccessibility?.();
    drawer?.bindOverlay?.();
    drawer?.upgradeTrustProof?.();
  });
})();
